import React, { useState, useEffect, useCallback, useMemo } from "react";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { FormWrapper, Form } from "../../../components/CustomForm/FormStyles";
import { MODALIDADES_PSS } from "../utils/HospitalizacionConstant";
import { validarFormularioHojaQuirurgica } from "../utils/ValidationUtils";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import { Separator } from "../../../components/Common/CommonStyles";
import useEmpresa from "../../../hooks/useEmpresa";
import Colors from "../../../themes/Colors";
import Text from "../../../components/Common/Text";
import FileUploadComponent from "./FileUploadComponent";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import CoberturasMedicos from "./CoberturasMedicos";

const HojaQuirurgicaForm = ({
  onSubmit,
  onCancel,
  paciente,
  isLoading,
  getProcedimientosPssData,
  getProcedimientosPssLoading,
  getProcedimientosPssError,
  getMedicosData,
  getMedicosLoading,
  getMedicosError,
  consultarTipoPssNoMedicoData,
  getProcedimientosPss,
  getMedicos,
  obtenerEspecialidadesMedicas,
  obtenerEspecialidadesMedicasData,
  obtenerEspecialidadesMedicasLoading,
  obtenerEspecialidadesMedicasError,
  coberturasEnviadas = new Set(),
  archivosEnviados = new Set(),
  coberturasCompletas = [],
}) => {
  const empresa = useEmpresa();
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationAlertConfig, setValidationAlertConfig] = useState(null);
  const [coberturasMedicos, setCoberturasMedicos] = useState([]);
  const [showCancelConfirmAlert, setShowCancelConfirmAlert] = useState(false);
  const [showCoberturaDuplicadaAlert, setShowCoberturaDuplicadaAlert] =
    useState(false);
  const [showMaxMedicosAlert, setShowMaxMedicosAlert] = useState(false);

  const tipoReclamante = useMemo(
    () => consultarTipoPssNoMedicoData?.tipoReclamante || MODALIDADES_PSS.ARS,
    [consultarTipoPssNoMedicoData?.tipoReclamante]
  );

  const crearCoberturaPorDefecto = useCallback(() => {
    return {
      id: Date.now(),
      cobertura: null,
      modalidadPss:
        tipoReclamante === MODALIDADES_PSS.ARS
          ? MODALIDADES_PSS.ARS
          : tipoReclamante === MODALIDADES_PSS.PAQUETE
          ? MODALIDADES_PSS.PAQUETE
          : MODALIDADES_PSS.ARS,
      medico: null,
    };
  }, [tipoReclamante]);

  const handleFilesChange = useCallback((files) => {
    const filesWithMetadata = files.map((file, index) => {
      if (file.id && file.name && file.size && file.type) {
        return file;
      }
      return {
        id: file.id || `file_${Date.now()}_${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        url: file instanceof File ? URL.createObjectURL(file) : file.url,
        file: file,
      };
    });
    setAttachedFiles(filesWithMetadata);
  }, []);

  useEffect(() => {
    setCoberturasMedicos([crearCoberturaPorDefecto()]);
    setAttachedFiles([]);
  }, [paciente?.numeroIngreso, crearCoberturaPorDefecto]);

  const eliminarCoberturaMedico = useCallback(
    (id) => {
      const coberturasFiltradas = coberturasMedicos.filter(
        (item) => item.id !== id
      );

      if (coberturasFiltradas.length === 0) {
        setCoberturasMedicos([crearCoberturaPorDefecto()]);
      } else {
        setCoberturasMedicos(coberturasFiltradas);
      }
    },
    [coberturasMedicos, crearCoberturaPorDefecto]
  );

  const obtenerMedicosDisponibles = useCallback(
    (coberturaId) => {
      if (!coberturaId) return [];
      if (getMedicosData && !getMedicosLoading && !getMedicosError) {
        return getMedicosData.medicos || [];
      }
      return [];
    },
    [getMedicosData, getMedicosLoading, getMedicosError]
  );

  const actualizarCobertura = useCallback(
    (id, cobertura) => {
      let hayDuplicado = false;

      setCoberturasMedicos((prevCoberturas) => {
        const itemEncontrado = prevCoberturas.find((item) => item.id === id);

        if (!itemEncontrado) {
          return prevCoberturas;
        }

        if (!cobertura) {
          return prevCoberturas.map((item) =>
            item.id === id ? { ...item, cobertura: null } : item
          );
        }

        const medicoActual = itemEncontrado.medico;
        const codigoCobertura =
          cobertura.cobertura || cobertura.codigo || cobertura.id;

        if (codigoCobertura) {
          const existeDuplicado = coberturasCompletas.some(
            (coberturaGuardada) =>
              coberturaGuardada?.cobertura?.cobertura === codigoCobertura
          );

          if (existeDuplicado) {
            hayDuplicado = true;
            setShowCoberturaDuplicadaAlert(true);
            return prevCoberturas;
          }
        }

        if (medicoActual) {
          const coberturaId =
            cobertura.id || cobertura.codigo || cobertura.cobertura;
          const medicosDisponibles = obtenerMedicosDisponibles(coberturaId);
          const esCompatible =
            medicosDisponibles.length === 0 ||
            medicosDisponibles.some((m) => m.codigo === medicoActual.codigo);

          if (!esCompatible && medicosDisponibles.length > 0) {
            return prevCoberturas.map((item) =>
              item.id === id ? { ...item, cobertura, medico: null } : item
            );
          }
        }

        return prevCoberturas.map((item) =>
          item.id === id ? { ...item, cobertura } : item
        );
      });

      return hayDuplicado;
    },
    [obtenerMedicosDisponibles, coberturasCompletas]
  );

  const actualizarMedico = useCallback(
    (id, medico) => {
      setCoberturasMedicos((prevCoberturas) => {
        const itemEncontrado = prevCoberturas.find((item) => item.id === id);

        if (!itemEncontrado) {
          return prevCoberturas;
        }

        if (medico && itemEncontrado.cobertura) {
          const coberturaId =
            itemEncontrado.cobertura.id ||
            itemEncontrado.cobertura.codigo ||
            itemEncontrado.cobertura.cobertura;
          const medicosDisponibles = obtenerMedicosDisponibles(coberturaId);

          if (medicosDisponibles.length > 0) {
            const esCompatible = medicosDisponibles.some(
              (m) => m.codigo === medico.codigo
            );

            if (!esCompatible) {
              return prevCoberturas.map((item) =>
                item.id === id ? { ...item, medico: null } : item
              );
            }
          }
        }

        const medicoActualizado = medico
          ? { ...medico, nombre: medico.nombre || medico.descripcion || "" }
          : null;

        return prevCoberturas.map((item) =>
          item.id === id ? { ...item, medico: medicoActualizado } : item
        );
      });
    },
    [obtenerMedicosDisponibles]
  );

  const actualizarModalidadPss = useCallback((id, nuevaModalidad) => {
    setCoberturasMedicos((prevCoberturas) =>
      prevCoberturas.map((item) =>
        item.id === id ? { ...item, modalidadPss: nuevaModalidad } : item
      )
    );
  }, []);

  const actualizarNombreMedico = useCallback((id, nombre) => {
    setCoberturasMedicos((prevCoberturas) =>
      prevCoberturas.map((item) =>
        item.id === id
          ? {
              ...item,
              medico: { ...(item.medico || {}), nombre },
            }
          : item
      )
    );
  }, []);

  const actualizarEspecialidadMedico = useCallback(
    (id, especialidad, codigoEspecialidad) => {
      setCoberturasMedicos((prevCoberturas) =>
        prevCoberturas.map((item) =>
          item.id === id
            ? {
                ...item,
                medico: {
                  ...(item.medico || {}),
                  especialidad,
                  codigoEspecialidad,
                },
              }
            : item
        )
      );
    },
    []
  );

  const reiniciarCamposMedico = useCallback((id) => {
    setCoberturasMedicos((prevCoberturas) =>
      prevCoberturas.map((item) =>
        item.id === id ? { ...item, medico: null, cobertura: null } : item
      )
    );
  }, []);

  const validateFormHojaQuirurgica = useCallback(() => {
    const codigosMedicosUnicos = new Set();

    coberturasCompletas.forEach((cobertura) => {
      const codigoCobertura = cobertura?.cobertura?.cobertura;
      const codigoMedico = cobertura?.medico?.codigo;
      if (codigoMedico && codigoCobertura !== "0") {
        codigosMedicosUnicos.add(codigoMedico);
      }
    });

    coberturasMedicos.forEach((cobertura) => {
      const codigoCobertura = cobertura?.cobertura?.cobertura;
      const codigoMedico = cobertura?.medico?.codigo;
      if (codigoMedico && codigoCobertura !== "0") {
        codigosMedicosUnicos.add(codigoMedico);
      }
    });

    if (codigosMedicosUnicos.size > 5) {
      setShowMaxMedicosAlert(true);
      return false;
    }

    const hojaData = {
      attachedFiles,
      coberturasMedicos,
      modalidadPss: tipoReclamante,
    };

    const validationResult = validarFormularioHojaQuirurgica(hojaData);

    if (!validationResult.isValid) {
      const firstError = validationResult.errors[0];
      setValidationAlertConfig({
        type: firstError.type,
        title: firstError.title,
        message: firstError.message,
        onConfirm: () => setShowValidationAlert(false),
      });
      setShowValidationAlert(true);
      return false;
    }

    return true;
  }, [coberturasCompletas, coberturasMedicos, attachedFiles, tipoReclamante]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!validateFormHojaQuirurgica()) {
        return;
      }

      const primeraCobertura = coberturasMedicos?.[0];
      const codigoCobertura = primeraCobertura?.cobertura?.cobertura || "";

      const archivosConCobertura = attachedFiles.map((archivo) => ({
        ...archivo,
        codigoCobertura: archivo.codigoCobertura || codigoCobertura,
      }));

      const hojaData = {
        numeroIngreso: paciente?.numeroIngreso,
        attachedFiles: archivosConCobertura,
        coberturasMedicos: coberturasMedicos,
        modalidadPss: tipoReclamante,
      };

      if (onSubmit) {
        onSubmit(hojaData);
      }
    },
    [
      validateFormHojaQuirurgica,
      coberturasMedicos,
      attachedFiles,
      paciente?.numeroIngreso,
      tipoReclamante,
      onSubmit,
    ]
  );

  const handleCancelClick = useCallback(() => {
    const tieneArchivosAdjuntos = attachedFiles && attachedFiles.length > 0;

    const tieneCoberturasConDatos = coberturasMedicos?.some((item) => {
      const tieneCobertura =
        item.cobertura !== null && item.cobertura !== undefined;
      const tieneMedico = item.medico !== null && item.medico !== undefined;
      const tieneEspecialidad =
        item.medico?.especialidad || item.medico?.codigoEspecialidad;
      const tieneNombreMedico = item.medico?.nombre;

      return (
        tieneCobertura || tieneMedico || tieneEspecialidad || tieneNombreMedico
      );
    });

    if (tieneArchivosAdjuntos || tieneCoberturasConDatos) {
      setShowCancelConfirmAlert(true);
    } else if (onCancel) {
      onCancel();
    }
  }, [attachedFiles, coberturasMedicos, onCancel]);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelConfirmAlert(false);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  return (
    <FormWrapper
      borderColor={Colors.transparent}
      testid="hoja-quirurgica-form-wrapper"
    >
      <Text
        size="24px"
        weight="bold"
        color={empresa?.COLOR_PRINCIPAL}
        testid="hoja-quirurgica-title"
      >
        Formulario de coberturas y anexos
      </Text>

      <Form onSubmit={handleSubmit}>
        <Section
          align="left"
          display="flex"
          flexDirection="column"
          width="100%"
          margin="0 0 20px 0"
        >
          <Text
            size="18px"
            weight="600"
            color={empresa?.COLOR_PRINCIPAL}
            margin="0 0 15px 0"
          >
            Coberturas con médicos participantes
          </Text>
          <CoberturasMedicos
            coberturasMedicos={coberturasMedicos}
            onEliminarCobertura={eliminarCoberturaMedico}
            onActualizarCobertura={actualizarCobertura}
            onActualizarMedico={actualizarMedico}
            onActualizarModalidadPss={actualizarModalidadPss}
            onActualizarNombreMedico={actualizarNombreMedico}
            onActualizarEspecialidadMedico={actualizarEspecialidadMedico}
            obtenerMedicosDisponibles={obtenerMedicosDisponibles}
            onReiniciarCampos={reiniciarCamposMedico}
            getProcedimientosPssData={getProcedimientosPssData}
            getProcedimientosPssLoading={getProcedimientosPssLoading}
            getProcedimientosPssError={getProcedimientosPssError}
            getMedicosData={getMedicosData}
            getMedicosLoading={getMedicosLoading}
            getMedicosError={getMedicosError}
            manejarBusquedaProcedimiento={getProcedimientosPss}
            manejarBusquedaMedico={getMedicos}
            consultarTipoPssNoMedicoData={consultarTipoPssNoMedicoData}
            obtenerEspecialidadesMedicas={obtenerEspecialidadesMedicas}
            obtenerEspecialidadesMedicasData={obtenerEspecialidadesMedicasData}
            obtenerEspecialidadesMedicasLoading={
              obtenerEspecialidadesMedicasLoading
            }
            obtenerEspecialidadesMedicasError={
              obtenerEspecialidadesMedicasError
            }
            testid="coberturas-medicos-component"
          />
        </Section>

        <Section
          align="left"
          display="flex"
          flexDirection="column"
          width="100%"
          margin="0 0 20px 0"
          testid="file-upload-section"
        >
          <Text
            size="18px"
            weight="600"
            color={empresa?.COLOR_PRINCIPAL}
            margin="0 0 15px 0"
            testid="file-upload-title"
          >
            Adjuntar Archivos ({attachedFiles.length}/10)
          </Text>
          <FileUploadComponent
            onFilesChange={handleFilesChange}
            maxFiles={10}
            maxSizeMB={7.78}
            initialFiles={attachedFiles}
            archivosEnviados={archivosEnviados}
            testid="file-upload-component"
          />
        </Section>

        <Section
          align="center"
          display="flex"
          justify="end"
          width="100%"
          testid="buttons-section"
        >
          <CustomButton
            label="Guardar"
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            height="46px"
            width="200px"
            testid="btn-guardar"
          />
          <Separator width="20px" testid="separator-buttons" />
          <CustomButton
            label="Cancelar"
            onClick={handleCancelClick}
            disabled={isLoading}
            height="46px"
            width="200px"
            testid="btn-cancelar"
            backgroundColor={Colors.gray}
          />
        </Section>
      </Form>

      <CheckRender allowed={showValidationAlert}>
        <CustomAlert {...validationAlertConfig} />
      </CheckRender>

      <CheckRender allowed={showCancelConfirmAlert}>
        <CustomAlert
          type="confirm"
          title="Confirmar cancelación"
          message="Si sale ahora, los datos ingresados se perderán. ¿Desea continuar?"
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowCancelConfirmAlert(false)}
          confirmText="Sí, salir"
          cancelText="No, quedarse"
          maxWidth="500px"
        />
      </CheckRender>

      <CheckRender allowed={showCoberturaDuplicadaAlert}>
        <CustomAlert
          type="error"
          title="Cobertura duplicada"
          message="Esta cobertura ya existe en la lista de coberturas a enviar."
          onConfirm={() => setShowCoberturaDuplicadaAlert(false)}
          confirmText="Entendido"
          maxWidth="500px"
        />
      </CheckRender>

      <CheckRender allowed={showMaxMedicosAlert}>
        <CustomAlert
          type="error"
          title="Límite de médicos excedido"
          message="No se pueden agregar más de 5 médicos diferentes en el formulario. Por favor, elimine algunas coberturas o modifique las existentes para reducir el número de médicos únicos."
          onConfirm={() => setShowMaxMedicosAlert(false)}
          confirmText="Entendido"
          maxWidth="500px"
        />
      </CheckRender>
    </FormWrapper>
  );
};

export default HojaQuirurgicaForm;
